import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import {
  API_BASE_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import ReadingComprehensionTest from '../../../components/ReadingComprehensionTest';
import { toReadingAnswerIndices } from '../../../components/readingComprehensionReview';
import { clearReadingProgressCache } from './api/progressAPI';

const buildAuthAwareReadingError = async (response, fallbackMessage) => {
  if (response.status === 404) {
    return new Error('Test not found');
  }

  if (response.status === 401) {
    return new Error('Authentication failed');
  }

  return new Error(fallbackMessage);
};

// API function to get specific passage with questions
const getPassageWithQuestions = async (passageId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/gre/reading/passage/${passageId}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareReadingError(
        response,
        'Failed to fetch passage data'
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching passage:', error);
    throw error;
  }
};

// API function to save passage completion
const savePassageCompletion = async (
  passageId,
  score,
  correctAnswers,
  totalQuestions,
  timeSpent,
  readingTime,
  answers,
  questions
) => {
  try {
    const user = getStoredUser();
    const userEmail = user?.email;

    const formattedAnswers = (questions || []).map((questionData, index) => {
      const userAnswer = toReadingAnswerIndices(questionData, answers?.[index]);

      return {
        question_id: questionData?.id,
        user_answer: userAnswer,
        timestamp: new Date().toISOString(),
      };
    }).filter((item) => item.question_id != null);

    const response = await fetch(
      `${API_BASE_URL}/api/gre/reading/passage-completion`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          passage_id: passageId,
          score,
          correct_answers: correctAnswers,
          total_questions: totalQuestions,
          time_spent_seconds: timeSpent,
          reading_time_seconds: readingTime,
          answers: formattedAnswers,
        }),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareReadingError(
        response,
        'Failed to save passage completion'
      );
    }

    clearReadingProgressCache(userEmail);
    return await response.json();
  } catch (error) {
    console.error('Error saving passage completion:', error);
    throw error;
  }
};

const getPassageReview = async (passageId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/gre/reading/passage/${passageId}/review`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw await buildAuthAwareReadingError(
      response,
      'Failed to fetch passage review'
    );
  }

  return await response.json();
};

// API function to get user's answers for a passage
const getUserPassageAnswers = async (passageId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/gre/reading/passage/${passageId}/user-answers`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareReadingError(
        response,
        'Failed to fetch user answers'
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching user answers:', error);
    throw error;
  }
};

export default function GREReadingComprehensionTest() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const isReviewMode = searchParams.get('review') === 'true';

  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingResult, setExistingResult] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  // Refs to track ongoing API requests
  const ongoingRequests = useRef({});

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
      setToast,
    });
  };

  // Helper function to display toast
  const showToast = (message, success = true) => {
    setToast({ show: true, message, success });
    setTimeout(() => {
      setToast({ show: false, message: '', success: true });
    }, 3000);
  };

  // Enhanced API functions with duplicate prevention
  const getPassageWithQuestionsWithRef = async (passageId) => {
    const requestKey = `passage_${passageId}`;

    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    const requestPromise = getPassageWithQuestions(passageId);
    ongoingRequests.current[requestKey] = requestPromise;

    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  const getUserPassageAnswersWithRef = async (passageId) => {
    const requestKey = `user_answers_${passageId}`;

    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    const requestPromise = getUserPassageAnswers(passageId);
    ongoingRequests.current[requestKey] = requestPromise;

    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  // Load test data from backend
  useEffect(() => {
    const loadTestData = async () => {
      try {
        setLoading(true);
        setError(null);
        setTestData(null);
        setExistingReview(null);
        setExistingResult(null);

        // Try to get passage data first
        let passageData;
        try {
          passageData = await getPassageWithQuestionsWithRef(testId);
        } catch (err) {
          if (err.message === 'Test not found') {
            setError('Test not found');
            return;
          }
          throw err;
        }

        // Transform backend data to match component structure
        const transformedTestData = {
          id: passageData.id,
          title: passageData.title,
          description: passageData.description,
          difficulty: passageData.difficulty,
          duration: `${passageData.duration_minutes} minutes`,
          questions: passageData.questions.length,
          topic: passageData.topic,
          totalTimeMs: passageData.duration_minutes * 60 * 1000,
          expectedReadingTimeMs:
            passageData.expected_reading_time_minutes * 60 * 1000,
          passage: passageData.passage_text,
          questions_data: passageData.questions.map((q) => ({
            id: q.id,
            type: q.question_type,
            question: q.question_text,
            options: q.options,
            correct_answers: q.correct_answers,
            explanation: q.explanation,
            gre_skill: q.gre_skill,
            gre_skill_label: q.gre_skill_label,
          })),
        };

        setTestData(transformedTestData);

        if (isReviewMode) {
          try {
            const review = await getPassageReview(testId);
            setExistingReview(review);
          } catch (reviewError) {
            console.error('Error fetching passage review:', reviewError);
          }
          const results = await getUserPassageAnswersWithRef(testId);
          setExistingResult(results);
        }
      } catch (error) {
        console.error('Error loading test data:', error);
        if (error?.message === 'Authentication failed') {
          redirectToLogin();
          return;
        }
        setError(error.message || 'Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    loadTestData();
  }, [testId, isReviewMode]);

  // Handler for saving completion
  const handleSaveCompletion = async ({
    score,
    correctAnswers,
    totalQuestions,
    timeSpent,
    readingTime,
    answers,
    questions,
  }) => {
    try {
      await savePassageCompletion(
        testId,
        score,
        correctAnswers,
        totalQuestions,
        timeSpent,
        readingTime,
        answers,
        questions
      );

      // Dispatch event to notify other components that test is completed
      window.dispatchEvent(
        new CustomEvent('greReadingTestCompleted', {
          detail: {
            passageId: testId,
            score,
            correctAnswers,
            totalQuestions,
          },
        })
      );

      console.log('✅ Test completion saved and event dispatched');
      try {
        return await getPassageReview(testId);
      } catch (reviewError) {
        console.error('Error fetching passage review after save:', reviewError);
        return null;
      }

      // Show success toast
      showToast(
        `Test completed! Score: ${score}% (${correctAnswers}/${totalQuestions})`,
        true
      );
    } catch (error) {
      console.error('❌ Error saving test completion:', error);
      if (error?.message === 'Authentication failed') {
        redirectToLogin();
        return;
      }

      // Show error toast
      showToast('Error saving your progress. Please try again.', false);

      // Still dispatch event even if save failed (data might be in localStorage)
      window.dispatchEvent(
        new CustomEvent('greReadingTestCompleted', {
          detail: { passageId: testId },
        })
      );
    }
  };

  // Handler for exiting test
  const handleExit = () => {
    navigate('/exams/gre/', { state: { activeCategory: 'reading' } });
  };

  return (
    <>
      {toast.show && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            backgroundColor: toast.success
              ? 'var(--liwaza-success, #10b981)'
              : 'var(--liwaza-error, #ef4444)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideInRight 0.3s ease-out',
            fontWeight: 500,
          }}
        >
          <FiCheck size={18} />
          <span>{toast.message}</span>
        </div>
      )}
      <ReadingComprehensionTest
        testData={testData}
        loading={loading}
        error={error}
        isReviewMode={isReviewMode}
        existingResult={existingResult}
        initialReview={existingReview}
        passageId={testId}
        onFetchReview={() => getPassageReview(testId)}
        onSaveCompletion={handleSaveCompletion}
        onExit={handleExit}
        spaceName="Verbal vault"
        examType="GRE"
      />
    </>
  );
}
